import { Box, FormControl, Select, Typography, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import History from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';
import NotesIcon from '@mui/icons-material/Note';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckIcon from '@mui/icons-material/Check';

import { Channels } from '@bandido/shared/constants';

import { StyledMenuItem, MenuItemContent, StyledListItemText } from './styles';

interface CaseContentFilterBarProps {
  filterTab: Channels | null;
  handleChangeFilterTab: (value: Channels | null) => void;
}

export const CaseContentFilterBar = ({ filterTab, handleChangeFilterTab }: CaseContentFilterBarProps) => {
  const { t } = useTranslation();

  const handleSelectChange = (event: any) => {
    const value = event.target.value === 'all' ? null : event.target.value;
    handleChangeFilterTab(value);
  };

  const getSelectedIcon: Record<Channels | 'default', React.ReactElement> = {
    [Channels.EMAIL]: <EmailIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    [Channels.PHONE]: <PhoneIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    [Channels.WHATSAPP]: <WhatsAppIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    [Channels.NOTES]: <NotesIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    [Channels.CASE_LOG]: <History sx={{ color: (theme) => theme.palette.primary.main }} />,
    default: <History sx={{ color: (theme) => theme.palette.primary.main }} />,
  };

  const getSelectedLabel: Record<Channels | 'default', string> = {
    [Channels.EMAIL]: t('cases.view.content.email'),
    [Channels.PHONE]: t('cases.view.content.phone'),
    [Channels.WHATSAPP]: t('cases.view.content.whatsapp'),
    [Channels.NOTES]: t('cases.view.content.notes'),
    [Channels.CASE_LOG]: t('cases.view.content.history'),
    default: t('cases.view.content.all'),
  };

  const checkMark = <CheckIcon sx={{ color: (theme) => theme.palette.primary.main }} />;

  const getMenuIconColor = (theme: Theme, channel: Channels | 'all') => {
    if (filterTab === channel || (filterTab === null && channel === 'all')) {
      return theme.palette.primary.main;
    }
    return theme.palette.grey[600];
  };

  return (
    <Box>
      <Box display="flex" gap={1} alignItems="center">
        <Typography variant="subtitle2">{t('cases.view.content.show')}:</Typography>
        <FormControl variant="outlined">
          <Select
            sx={{ minWidth: 220, borderRadius: 1 }}
            size="small"
            value={filterTab || 'all'}
            onChange={handleSelectChange}
            IconComponent={ArrowDropDown}
            displayEmpty
            renderValue={() => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getSelectedIcon[filterTab || 'default']}
                <span>{getSelectedLabel[filterTab || 'default']}</span>
              </Box>
            )}
          >
            <StyledMenuItem value="all">
              <MenuItemContent>
                <History sx={{ color: (theme) => getMenuIconColor(theme, 'all') }} />
                <StyledListItemText primary={t('cases.view.content.all')} />
              </MenuItemContent>
              {filterTab === null && checkMark}
            </StyledMenuItem>

            <StyledMenuItem value={Channels.EMAIL}>
              <MenuItemContent>
                <EmailIcon sx={{ color: (theme) => getMenuIconColor(theme, Channels.EMAIL) }} />
                <StyledListItemText primary={t('cases.view.content.email')} />
              </MenuItemContent>
              {filterTab === Channels.EMAIL && checkMark}
            </StyledMenuItem>

            <StyledMenuItem value={Channels.PHONE}>
              <MenuItemContent>
                <PhoneIcon sx={{ color: (theme) => getMenuIconColor(theme, Channels.PHONE) }} />
                <StyledListItemText primary={t('cases.view.content.phone')} />
              </MenuItemContent>
              {filterTab === Channels.PHONE && checkMark}
            </StyledMenuItem>

            <StyledMenuItem value={Channels.WHATSAPP}>
              <MenuItemContent>
                <WhatsAppIcon sx={{ color: (theme) => getMenuIconColor(theme, Channels.WHATSAPP) }} />
                <StyledListItemText primary={t('cases.view.content.whatsapp')} />
              </MenuItemContent>
              {filterTab === Channels.WHATSAPP && checkMark}
            </StyledMenuItem>

            <StyledMenuItem value={Channels.NOTES}>
              <MenuItemContent>
                <NotesIcon sx={{ color: (theme) => getMenuIconColor(theme, Channels.NOTES) }} />
                <StyledListItemText primary={t('cases.view.content.notes')} />
              </MenuItemContent>
              {filterTab === Channels.NOTES && checkMark}
            </StyledMenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};
